import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  borderGrey,
  darkerBG,
  lightRedBG,
  mediumText,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../../styling/stylingHelper';
import { FrontendStaffUser } from '../../../../services/staff/staffService';
import StaffAvatar from './StaffAvatar';
import { InventorySlideOver, slideOverCancelClass, slideOverDangerClass } from '../../../ui/InventorySlideOver';

interface DeleteUserModalProps {
  isOpen: boolean;
  user: FrontendStaffUser | null;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteUserModal: React.FC<DeleteUserModalProps> = ({ isOpen, user, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const displayName = user?.name?.trim() || user?.email || '';

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('staff.deleteUserLink')}
      description={t('staff.deleteConfirmation')}
      closeLabel={t('common.close')}
      footer={
        <>
          <button type="button" onClick={onClose} className={slideOverCancelClass}>
            {t('staff.cancel')}
          </button>
          <button type="button" onClick={onConfirm} className={slideOverDangerClass} disabled={!user}>
            {t('staff.deleteUserButton')}
          </button>
        </>
      }
    >
      {/* Irreversibility warning — sits above the details so it is read before the
          confirm button, not after. */}
      <div
        className={cn(
          'flex items-start gap-3 p-4 rounded-lg border mb-6',
          lightRedBG,
          'border-red-200 dark:border-red-800'
        )}
      >
        <span
          className="grid place-items-center h-8 w-8 shrink-0 rounded-lg bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400"
          aria-hidden
        >
          <AlertTriangle className="w-[18px] h-[18px]" />
        </span>
        <p className={cn(smallTextSize, semiBoldText, 'text-red-700 dark:text-red-300 leading-6')}>
          {t('staff.cannotBeUndone')}
        </p>
      </div>

      {user && (
        <div>
          <p className={cn(secondaryText, mediumText, 'text-[16px] leading-7')}>{t('staff.staffInformation')}</p>

          <div className={cn('mt-6 space-y-4 p-4 rounded-lg border mb-6', borderGrey, darkerBG)}>
            <div className={cn('flex items-center gap-3 mb-4 pb-4 border-b', borderGrey)}>
              <StaffAvatar user={user} size="md" />
              <div className="min-w-0">
                <p className={cn('text-[18px] leading-6 truncate', semiBoldText, primaryText)}>{displayName}</p>
                <p className={cn(secondaryText, 'text-[14px] leading-6 font-normal truncate mt-1')}>{user.email}</p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className={cn(secondaryText, 'text-[14px] leading-5 font-normal')}>{t('staff.currentRole')}</span>
              <span className={cn('text-[14px] leading-5', semiBoldText, primaryText)}>
                {user.restaurant_role || '—'}
              </span>
            </div>
          </div>
        </div>
      )}
    </InventorySlideOver>
  );
};

export default DeleteUserModal;
